"use client";

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '@/components/ui/toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';

export default function ReportModal({ open, onClose, target }: { open: boolean; onClose: () => void; target: { id: string | number; type: 'post' | 'video' | 'gallery' | 'image' | 'profile' } | null }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const ref = useRef<HTMLDivElement | null>(null);

  const textRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { try { textRef.current?.focus(); } catch {} }, 20);
    return () => clearTimeout(t);
  }, [open]);

  async function submit() {
    if (!target || !reason.trim()) return;
    setBusy(true);
    try {
      if (target.type === 'profile') {
        // Report a user profile via chat moderation/report endpoint requiring userId
        const res = await fetch(`/api/proxy/api/chat/report`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: String(target.id), reason })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast({ title: 'Report submitted', duration: 2500 });
        try { track('report', { type: 'profile', id: String(target.id) }); } catch {}
        onClose(); setReason('');
        return;
      }
      const root = target.type === 'video' ? `/api/video/v2/${encodeURIComponent(String(target.id))}`
                 : target.type === 'post' ? `/api/post/${encodeURIComponent(String(target.id))}`
                 : target.type === 'image' ? `/api/image/${encodeURIComponent(String(target.id))}`
                 : `/api/gallery/${encodeURIComponent(String(target.id))}`;
      const res = await fetch(`/api/proxy${root}/report`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: 'Report submitted', duration: 2500 });
      try { track('report', { type: target.type, id: String(target.id) }); } catch {}
      onClose(); setReason('');
    } catch (e: any) {
      toast({ title: 'Failed to submit report', description: e?.message || '', variant: 'destructive' });
    } finally { setBusy(false); }
  }

  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report content</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Please describe the issue briefly.</p>
        <textarea
          ref={textRef}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          className="mt-2 w-full border rounded p-2 bg-background text-foreground"
          rows={4}
          placeholder="Why are you reporting this?"
          aria-label="Report reason"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length === 0}>{busy ? 'Submitting…' : 'Submit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
